import apiClient from "./apiClient"

// ── Outlet Payment Methods ──

export async function getOutletPaymentMethods(
    companyId: string,
    outletId: string,
    options: { enabledOnly?: boolean } = {}
) {
    const params = new URLSearchParams()
    if (options.enabledOnly) params.append("enabledOnly", "true")

    const response = await apiClient.get(
        `/api/companies/${companyId}/outlets/${outletId}/payment-methods?${params.toString()}`
    )
    return response.paymentMethods
}

export async function updateOutletPaymentMethods(
    companyId: string,
    outletId: string,
    methods: Array<{
        payment_method_id: string
        is_enabled: boolean
        display_order?: number
    }>
) {
    const response = await apiClient.put(
        `/api/companies/${companyId}/outlets/${outletId}/payment-methods`,
        { methods }
    )
    return response.paymentMethods
}

// ── Outlet VAT Rates ──

export async function getOutletVatRates(
    companyId: string,
    outletId: string,
    options: { enabledOnly?: boolean } = {}
) {
    const params = new URLSearchParams()
    if (options.enabledOnly) params.append("enabledOnly", "true")

    const response = await apiClient.get(
        `/api/companies/${companyId}/outlets/${outletId}/vat-rates?${params.toString()}`
    )
    return response.vatRates
}

export async function updateOutletVatRates(
    companyId: string,
    outletId: string,
    rates: Array<{
        vat_rate_id: string
        is_enabled: boolean
        display_order?: number
    }>
) {
    const response = await apiClient.put(
        `/api/companies/${companyId}/outlets/${outletId}/vat-rates`,
        { rates }
    )
    return response.vatRates
}

export async function createVatRate(
    companyId: string,
    data: { id?: string; label: string; percentage: number; is_active?: boolean }
) {
    const response = await apiClient.post(
        `/api/companies/${companyId}/vat-rates`,
        data
    )
    return response.vatRate
}

export async function deleteVatRate(companyId: string, vatRateId: string) {
    const response = await apiClient.delete(
        `/api/companies/${companyId}/vat-rates/${encodeURIComponent(vatRateId)}`
    )
    return response.vatRate
}

// ── Outlet Document Settings ──

export async function getOutletDocumentSettings(
    companyId: string,
    outletId: string
) {
    const response = await apiClient.get(
        `/api/companies/${companyId}/outlets/${outletId}/document-settings`
    )
    return response.settings
}

export async function upsertOutletDocumentSettings(
    companyId: string,
    outletId: string,
    data: Record<string, any>
) {
    const response = await apiClient.put(
        `/api/companies/${companyId}/outlets/${outletId}/document-settings`,
        data
    )
    return response.settings
}

// ── Logo ──

export async function uploadOutletLogo(
    companyId: string,
    outletId: string,
    file: File
) {
    const formData = new FormData()
    formData.append("logo", file)

    const response = await apiClient.put(
        `/api/companies/${companyId}/outlets/${outletId}/document-settings/logo`,
        formData
    )

    if (!response.ok) {
        const err = await response
            .json()
            .catch(() => ({ error: "Upload failed" }))
        throw new Error(err.error || "Upload failed")
    }

    return response.json()
}

export async function removeOutletLogo(companyId: string, outletId: string) {
    const response = await apiClient.delete(
        `/api/companies/${companyId}/outlets/${outletId}/document-settings/logo`
    )
    return response
}

// ── Document Preview ──

export async function previewOutletDocument(
    companyId: string,
    outletId: string,
    settings: Record<string, any>
): Promise<string> {
    const response = await apiClient.post(
        `/api/companies/${companyId}/outlets/${outletId}/document-settings/preview`,
        JSON.stringify(settings)
    )

    if (!response.ok) {
        throw new Error("Preview generation failed")
    }

    return response.text()
}
